import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FILE_TYPE, SIMULATION_TYPE } from "src/constant/common";
import { useSetRecoilState } from "recoil";
import { uploadedFilesState } from "src/recoil/atoms/uploadedFilesState";
import { cloneFile, getFileType, isDuplicateFileName } from "src/utils/common";
import classNames from "classnames";
import { uploadedFiles6DOFState } from "src/recoil/atoms/flightSimulator";

const UploadMoreFiles = ({
  className,
  onFileSelect,
  fieldName,
  isDisabled,
  fileTypeAllow = [FILE_TYPE.JSON, FILE_TYPE.CSV, FILE_TYPE.XLSX],
  checkDuplicatedFileNames = false,
  initialFiles = [],
  simulationType = SIMULATION_TYPE["3DOF"],
  sameFileTypeRequired = false,
}) => {
  const fileInputRef = useRef(null);
  const { t } = useTranslation("flight-simulator");
  const setUploadedFiles = useSetRecoilState(uploadedFilesState);
  const setUploadedFiles6DOF = useSetRecoilState(uploadedFiles6DOFState);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClick = () => {
    if (!isDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSaveFilesUploaded = (fileData, simulationType) => {
    if (simulationType === SIMULATION_TYPE["3DOF"]) {
      setUploadedFiles((prev) => {
        const updatedFiles = [...prev];

        fileData.forEach((newFile) => {
          updatedFiles.push(newFile);
        });

        return updatedFiles;
      });
    }
    if (simulationType === SIMULATION_TYPE["6DOF"]) {
      setUploadedFiles6DOF((prev) => {
        const updatedFiles = [...prev];

        fileData.forEach((newFile) => {
          updatedFiles.push(newFile);
        });

        return updatedFiles;
      });
    }
  };

  const getDuplicateFileNameErrorMessage = (files, initialFiles) => {
    const fileUploadedDuplicate = files.filter((file, index) => {
      return files.findIndex((f) => f.name === file.name) !== index;
    });

    if (fileUploadedDuplicate.length > 0) {
      return t("new.validate_duplicate_file_name_in_files_upload");
    }

    const duplicateFiles = files.filter((file) => isDuplicateFileName(initialFiles, file.name));

    if (duplicateFiles.length > 0) {
      return t("new.validate_duplicate_file_name");
    }
  };

  const getSameFileTypeErrorMessage = (files, initialFiles) => {
    const allFiles = initialFiles.map((file) => file.name).concat(files.map((file) => file.name));
    const firstFileType = getFileType(allFiles[0]);
    const hasDifferentTypes = allFiles.some((fileName) => getFileType(fileName) !== firstFileType);

    if (hasDifferentTypes) {
      return t("new.validate_same_file_type_required");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      if (e?.target?.files?.length > 10) {
        setErrorMessage(t("new.limit_attachments"));
        return;
      }

      if (checkDuplicatedFileNames) {
        const files = Array.from(e?.target?.files);

        const errorMsg = getDuplicateFileNameErrorMessage(files, initialFiles);
        if (errorMsg) {
          return setErrorMessage(errorMsg);
        }
      }
      if (sameFileTypeRequired) {
        const files = Array.from(e?.target?.files);

        const errorMsg = getSameFileTypeErrorMessage(files, initialFiles);
        if (errorMsg) {
          return setErrorMessage(errorMsg);
        }
      }

      setErrorMessage("");

      const allowedFiles = [];

      [...e?.target?.files].map((item) => {
        if (!fileTypeAllow.includes(getFileType(item.name))) {
          return setErrorMessage(
            t("new.validate_attachments_custom", {
              file_type: fileTypeAllow.join(", "),
            })
          );
        } else {
          const fileClone = cloneFile(item);
          return allowedFiles.push({
            file: fileClone,
            name: fileClone?.name,
            size: fileClone?.size,
            fieldName,
            uploadedAt: Date.now(),
          });
        }
      });

      if (allowedFiles.length > 0) {
        handleSaveFilesUploaded(allowedFiles, simulationType);

        const fileObjects = allowedFiles.map((file) => ({
          name: file.name,
          size: file.size,
        }));
        const fileData = initialFiles.concat(fileObjects);
        onFileSelect(fieldName, fileData);
      }

      // Reset the input value so the same file can be selected again
      e.target.value = null;
    }
  };

  const acceptFiles = () => fileTypeAllow.map((item) => `.${item}`).join();

  return (
    <div className={classNames("upload-more-files", className)}>
      <span className={`upload-file-button btn btn-secondary ${isDisabled ? "disabled" : ""}`} onClick={handleClick}>
        {t("new.select_file")}
      </span>
      <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} multiple accept={acceptFiles()} />
      {errorMessage && <div className="text-danger font-12 position-absolute">{errorMessage}</div>}
    </div>
  );
};

export default UploadMoreFiles;
